import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Save,
  Loader2,
  Plus,
  Trash2,
  ChevronRight,
  Image as ImageIcon,
  Sun,
  Camera,
  Users,
  Eye,
  Paintbrush,
  Maximize,
  Type,
  Layers,
  Palette,
  FileText,
  Settings,
  Sparkles,
  Globe,
  Copy,
} from "lucide-react";

interface Region {
  region_key: string;
  display_name: string;
}

interface PromptTemplate {
  id: string;
  section_key: string;
  section_label: string;
  content: any;
  updated_at: string;
  region_key: string | null;
}

const SECTION_ICONS: Record<string, any> = {
  interior_scenes: ImageIcon,
  exterior_facade_scenes: ImageIcon,
  exterior_garden_scenes: ImageIcon,
  lighting: Sun,
  camera_angles: Camera,
  household_types: Users,
  interaction_types: Sparkles,
  implied_signs: Eye,
  active_descriptions: Users,
  realism_levels: Paintbrush,
  prominence_rules: Maximize,
  size_rules: Type,
  blending_rules: Layers,
  quality_rules: Sparkles,
  format_rules: FileText,
  environment_rules: Settings,
  color_tone_matching: Palette,
  presence_templates: Users,
  edit_render: Paintbrush,
  format_variant_recompose: Maximize,
  format_variant_upscale: Maximize,
};

const SECTION_ORDER = [
  "format_rules",
  "environment_rules",
  "interior_scenes",
  "exterior_facade_scenes",
  "exterior_garden_scenes",
  "lighting",
  "camera_angles",
  "prominence_rules",
  "size_rules",
  "blending_rules",
  "color_tone_matching",
  "realism_levels",
  "presence_templates",
  "household_types",
  "interaction_types",
  "implied_signs",
  "active_descriptions",
  "quality_rules",
  "edit_render",
  "format_variant_recompose",
  "format_variant_upscale",
];

export default function PromptEditor() {
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>("");
  const [editedContent, setEditedContent] = useState<Record<string, any>>({});

  // Region state
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>("__global__");
  const [initializing, setInitializing] = useState(false);
  const [globalSectionKeys, setGlobalSectionKeys] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saved" | "error">("idle");

  useEffect(() => {
    loadRegions();
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [selectedRegion]);

  const loadRegions = async () => {
    const { data, error } = await supabase.from("regions").select("region_key, display_name").order("display_name");
    if (error) {
      console.error("Failed to load regions:", error);
      Sentry.captureException(error);
      return;
    }
    setRegions(data || []);
  };

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    const regionFilter = selectedRegion === "__global__" ? null : selectedRegion;

    // Always load global templates to know what sections exist
    const { data: globalData, error: globalError } = await supabase
      .from("prompt_templates")
      .select("*")
      .is("region_key", null)
      .order("section_key");

    if (globalError) {
      console.error("Failed to load templates:", globalError);
      Sentry.captureException(globalError);
      setError("Failed to load prompt templates.");
      setLoading(false);
      return;
    }

    const globalKeys = new Set((globalData || []).map(t => t.section_key));
    setGlobalSectionKeys(globalKeys);

    let data: any[] = globalData || [];

    if (regionFilter) {
      const { data: regionData, error: regionError } = await supabase
        .from("prompt_templates")
        .select("*")
        .eq("region_key", regionFilter)
        .order("section_key");

      if (regionError) {
        console.error("Failed to load region templates:", regionError);
        Sentry.captureException(regionError);
      }

      if (regionData && regionData.length > 0) {
        // Merge: region-specific overrides global
        const regionMap = new Map(regionData.map(t => [t.section_key, t]));
        data = (globalData || []).map(g => regionMap.get(g.section_key) || g);
      }
    }

    // Sort by defined order
    const sorted = data.sort((a, b) => {
      const ai = SECTION_ORDER.indexOf(a.section_key);
      const bi = SECTION_ORDER.indexOf(b.section_key);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    setTemplates(sorted);
    const initial: Record<string, any> = {};
    sorted.forEach((t) => (initial[t.section_key] = t.content));
    setEditedContent(initial);
    if (sorted.length > 0 && !activeSection) setActiveSection(sorted[0].section_key);
    setLoading(false);
  };

  const initializeRegion = async () => {
    if (selectedRegion === "__global__") return;
    setInitializing(true);
    setError(null);
    const { error } = await supabase.rpc("clone_templates_for_region", {
      target_region_key: selectedRegion,
    });
    if (error) {
      console.error("Failed to initialize region templates:", error);
      Sentry.captureException(error);
      setError("Failed to initialize region templates.");
    } else {
      await loadTemplates();
    }
    setInitializing(false);
  };

  const saveSection = async (sectionKey: string) => {
    setSaving(sectionKey);
    const content = editedContent[sectionKey];
    const regionFilter = selectedRegion === "__global__" ? null : selectedRegion;

    const query = supabase
      .from("prompt_templates")
      .update({ content, updated_at: new Date().toISOString() })
      .eq("section_key", sectionKey);

    if (regionFilter) {
      query.eq("region_key", regionFilter);
    } else {
      query.is("region_key", null);
    }

    const { error } = await query;

    if (error) {
      console.error("Failed to save template:", error);
      Sentry.captureException(error);
      setSaveStatus("error");
    } else {
      setTemplates((prev) =>
        prev.map((t) =>
          t.section_key === sectionKey ? { ...t, content, updated_at: new Date().toISOString() } : t
        )
      );
      setSaveStatus("saved");
    }
    setSaving(null);
    setTimeout(() => setSaveStatus("idle"), 3000);
  };

  const updateContent = (sectionKey: string, newContent: any) => {
    setEditedContent((prev) => ({ ...prev, [sectionKey]: newContent }));
  };

  const hasChanges = (sectionKey: string) => {
    const template = templates.find((t) => t.section_key === sectionKey);
    if (!template) return false;
    return JSON.stringify(template.content) !== JSON.stringify(editedContent[sectionKey]);
  };

  const activeTemplate = templates.find((t) => t.section_key === activeSection);
  const activeContent = editedContent[activeSection];
  const isRegionSelected = selectedRegion !== "__global__";
  const activeIsInherited = isRegionSelected && activeTemplate?.region_key === null;

  // Check if region has its own templates
  const regionHasTemplates = isRegionSelected && templates.some(t => t.region_key === selectedRegion);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-8">
      {/* Inline error/success banner */}
      {error && (
        <div className="mb-4 bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-center justify-between">
          <p className="text-sm text-destructive">{error}</p>
          <button onClick={() => setError(null)} className="text-destructive/60 hover:text-destructive text-sm">Dismiss</button>
        </div>
      )}
      {saveStatus === "saved" && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-700">Changes saved successfully.</p>
        </div>
      )}
      {saveStatus === "error" && (
        <div className="mb-4 bg-destructive/10 border border-destructive/20 rounded-lg p-3">
          <p className="text-sm text-destructive">Failed to save changes. Please try again.</p>
        </div>
      )}

      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">Prompt Template Editor</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Edit all prompt building blocks used for AI render generation
          </p>
        </div>

        {/* Region selector */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-muted-foreground" />
            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger className="w-[200px] h-9 text-sm">
                <SelectValue placeholder="Select region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__global__">🌍 Global (Default)</SelectItem>
                {regions.map((r) => (
                  <SelectItem key={r.region_key} value={r.region_key}>
                    {r.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isRegionSelected && !regionHasTemplates && (
            <Button
              variant="outline"
              size="sm"
              onClick={initializeRegion}
              disabled={initializing}
            >
              {initializing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Copy className="w-4 h-4 mr-2" />
              )}
              Initialize from Global
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-8 min-h-[calc(100vh-220px)]">
        {/* Sidebar nav */}
        <div className="w-64 shrink-0">
          <div className="bg-card border border-border rounded-xl p-2 sticky top-8 space-y-0.5 max-h-[calc(100vh-240px)] overflow-y-auto">
            {templates.map((t) => {
              const Icon = SECTION_ICONS[t.section_key] || FileText;
              const changed = hasChanges(t.section_key);
              const inherited = isRegionSelected && t.region_key === null;
              return (
                <button
                  key={t.section_key}
                  onClick={() => setActiveSection(t.section_key)}
                  className={cn(
                    "w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-all",
                    activeSection === t.section_key
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="truncate flex-1">{t.section_label}</span>
                  {inherited && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                      Global
                    </span>
                  )}
                  {changed && <span className="w-2 h-2 rounded-full bg-primary shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main editor */}
        <div className="flex-1 min-w-0">
          {activeTemplate && activeContent && (
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="font-display text-lg font-semibold">{activeTemplate.section_label}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Section: <code className="bg-muted px-1 rounded">{activeTemplate.section_key}</code>
                    {" · "}
                    Last saved: {new Date(activeTemplate.updated_at).toLocaleString()}
                    {activeIsInherited && (
                      <span className="ml-2 text-amber-500">⚠ Using global default — initialize region to customize</span>
                    )}
                  </p>
                </div>
                <Button
                  onClick={() => saveSection(activeSection)}
                  disabled={!hasChanges(activeSection) || saving === activeSection || activeIsInherited}
                  className="gradient-primary text-primary-foreground"
                >
                  {saving === activeSection ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              </div>

              <SectionEditor
                sectionKey={activeSection}
                content={activeContent}
                onChange={(c) => updateContent(activeSection, c)}
              />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Section-specific editors ──────────────────────────────────

interface SectionEditorProps {
  sectionKey: string;
  content: any;
  onChange: (content: any) => void;
}

function SectionEditor({ sectionKey, content, onChange }: SectionEditorProps) {
  // Detect content shape and render appropriate editor
  if (
    [
      "interior_scenes",
      "exterior_facade_scenes",
      "exterior_garden_scenes",
    ].includes(sectionKey)
  ) {
    return <SceneListEditor content={content} onChange={onChange} />;
  }

  if (sectionKey === "lighting") {
    return <LightingGridEditor content={content} onChange={onChange} />;
  }

  if (sectionKey === "camera_angles") {
    return <CameraEditor content={content} onChange={onChange} />;
  }

  if (
    [
      "household_types",
      "interaction_types",
      "prominence_rules",
      "color_tone_matching",
    ].includes(sectionKey)
  ) {
    return <KeyValueEditor content={content} onChange={onChange} />;
  }

  if (["implied_signs", "active_descriptions", "realism_levels"].includes(sectionKey)) {
    return <IndoorOutdoorEditor content={content} onChange={onChange} />;
  }

  if (["size_rules", "blending_rules", "environment_rules"].includes(sectionKey)) {
    return <GroupedListEditor content={content} onChange={onChange} />;
  }

  if (["format_rules", "quality_rules"].includes(sectionKey)) {
    return <RulesListEditor content={content} onChange={onChange} />;
  }

  if (sectionKey === "presence_templates") {
    return <PresenceTemplateEditor content={content} onChange={onChange} />;
  }

  // Fallback: raw JSON editor
  return <RawJsonEditor content={content} onChange={onChange} />;
}

// ─── Scene List Editor (scenario_key → string[]) ──────────────

function SceneListEditor({
  content,
  onChange,
}: {
  content: Record<string, string[]>;
  onChange: (c: any) => void;
}) {
  const keys = Object.keys(content);
  const [activeKey, setActiveKey] = useState(keys[0] || "");

  const updateItem = (key: string, index: number, value: string) => {
    const updated = { ...content, [key]: content[key].map((v, i) => (i === index ? value : v)) };
    onChange(updated);
  };

  const addItem = (key: string) => {
    onChange({ ...content, [key]: [...content[key], "New scene description..."] });
  };

  const removeItem = (key: string, index: number) => {
    onChange({ ...content, [key]: content[key].filter((_, i) => i !== index) });
  };

  const addKey = () => {
    const name = prompt("Enter new scenario key (e.g. 'beach_house'):");
    if (name && !content[name]) {
      onChange({ ...content, [name]: ["New scene description..."] });
      setActiveKey(name);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {keys.map((key) => (
          <button
            key={key}
            onClick={() => setActiveKey(key)}
            className={cn(
              "px-3 py-1.5 rounded-lg text-sm font-medium transition-all",
              activeKey === key
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            )}
          >
            {key.replace(/_/g, " ")}
          </button>
        ))}
        <button
          onClick={addKey}
          className="px-3 py-1.5 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:text-foreground border border-dashed border-border"
        >
          <Plus className="w-3 h-3 inline mr-1" />
          Add
        </button>
      </div>

      {activeKey && content[activeKey] && (
        <div className="space-y-3">
          {content[activeKey].map((desc: string, i: number) => (
            <div key={i} className="flex gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded bg-muted text-xs font-mono text-muted-foreground shrink-0 mt-1">
                v{i}
              </div>
              <Textarea
                value={desc}
                onChange={(e) => updateItem(activeKey, i, e.target.value)}
                className="min-h-[80px] text-sm"
              />
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 mt-1 text-muted-foreground hover:text-destructive"
                onClick={() => removeItem(activeKey, i)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => addItem(activeKey)}>
            <Plus className="w-3 h-3 mr-1" />
            Add Variation
          </Button>
        </div>
      )}
    </div>
  );
}

// ─── Lighting Grid (time_of_day → season → string) ───────────

function LightingGridEditor({
  content,
  onChange,
}: {
  content: Record<string, Record<string, string>>;
  onChange: (c: any) => void;
}) {
  const times = Object.keys(content);
  const seasons = times.length > 0 ? Object.keys(content[times[0]]) : [];

  const update = (time: string, season: string, value: string) => {
    onChange({
      ...content,
      [time]: { ...content[time], [season]: value },
    });
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="text-left text-xs font-medium text-muted-foreground p-2 w-28">
              Time / Season
            </th>
            {seasons.map((s) => (
              <th key={s} className="text-left text-xs font-medium text-muted-foreground p-2 capitalize">
                {s}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {times.map((time) => (
            <tr key={time} className="border-t border-border/50">
              <td className="p-2 text-sm font-medium capitalize align-top pt-3">
                {time.replace(/_/g, " ")}
              </td>
              {seasons.map((season) => (
                <td key={season} className="p-2">
                  <Textarea
                    value={content[time][season] || ""}
                    onChange={(e) => update(time, season, e.target.value)}
                    className="min-h-[60px] text-xs"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Camera Editor ─────────────────────────────────────────────

function CameraEditor({
  content,
  onChange,
}: {
  content: any;
  onChange: (c: any) => void;
}) {
  const updateArrayItem = (key: string, index: number, value: string) => {
    onChange({
      ...content,
      [key]: content[key].map((v: string, i: number) => (i === index ? value : v)),
    });
  };

  const updateString = (key: string, value: string) => {
    onChange({ ...content, [key]: value });
  };

  return (
    <div className="space-y-6">
      {Object.entries(content).map(([key, value]) => (
        <div key={key}>
          <h3 className="text-sm font-semibold capitalize mb-2">{key.replace(/_/g, " ")}</h3>
          {Array.isArray(value) ? (
            <div className="space-y-2">
              {(value as string[]).map((v, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="text-xs text-muted-foreground font-mono mt-2 w-6">{i}</span>
                  <Textarea
                    value={v}
                    onChange={(e) => updateArrayItem(key, i, e.target.value)}
                    className="min-h-[60px] text-sm"
                  />
                </div>
              ))}
            </div>
          ) : (
            <Textarea
              value={value as string}
              onChange={(e) => updateString(key, e.target.value)}
              className="min-h-[60px] text-sm"
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Key-Value Editor (key → string) ──────────────────────────

function KeyValueEditor({
  content,
  onChange,
}: {
  content: Record<string, string>;
  onChange: (c: any) => void;
}) {
  const addKey = () => {
    const name = prompt("Enter new key:");
    if (name && !content[name]) {
      onChange({ ...content, [name]: "Description..." });
    }
  };

  return (
    <div className="space-y-4">
      {Object.entries(content).map(([key, value]) => (
        <div key={key} className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium capitalize">{key.replace(/_/g, " ")}</label>
            <Button
              variant="ghost"
              size="icon"
              className="w-6 h-6 text-muted-foreground hover:text-destructive"
              onClick={() => {
                const { [key]: _, ...rest } = content;
                onChange(rest);
              }}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
          <Textarea
            value={value}
            onChange={(e) => onChange({ ...content, [key]: e.target.value })}
            className="min-h-[60px] text-sm"
          />
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addKey}>
        <Plus className="w-3 h-3 mr-1" />
        Add Entry
      </Button>
    </div>
  );
}

// ─── Indoor/Outdoor Editor (key → {indoor, outdoor}) ──────────

function IndoorOutdoorEditor({
  content,
  onChange,
}: {
  content: Record<string, { indoor: string; outdoor: string }>;
  onChange: (c: any) => void;
}) {
  return (
    <div className="space-y-6">
      {Object.entries(content).map(([key, value]) => (
        <div key={key} className="border border-border/50 rounded-lg p-4">
          <h3 className="text-sm font-semibold capitalize mb-3">{key.replace(/_/g, " ")}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Indoor</label>
              <Textarea
                value={value.indoor || ""}
                onChange={(e) =>
                  onChange({ ...content, [key]: { ...value, indoor: e.target.value } })
                }
                className="min-h-[80px] text-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Outdoor</label>
              <Textarea
                value={value.outdoor || ""}
                onChange={(e) =>
                  onChange({ ...content, [key]: { ...value, outdoor: e.target.value } })
                }
                className="min-h-[80px] text-sm"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Grouped List Editor (group → string[]) ───────────────────

function GroupedListEditor({
  content,
  onChange,
}: {
  content: Record<string, string[]>;
  onChange: (c: any) => void;
}) {
  return (
    <div className="space-y-6">
      {Object.entries(content).map(([group, items]) => (
        <div key={group}>
          <h3 className="text-sm font-semibold capitalize mb-3">{group.replace(/_/g, " ")}</h3>
          {Array.isArray(items) ? (
            <div className="space-y-2">
              {items.map((item: string, i: number) => (
                <div key={i} className="flex gap-2 items-start">
                  <Textarea
                    value={item}
                    onChange={(e) =>
                      onChange({
                        ...content,
                        [group]: items.map((v: string, j: number) =>
                          j === i ? e.target.value : v
                        ),
                      })
                    }
                    className="min-h-[50px] text-sm"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() =>
                      onChange({
                        ...content,
                        [group]: items.filter((_: string, j: number) => j !== i),
                      })
                    }
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onChange({ ...content, [group]: [...items, "New rule..."] })}
              >
                <Plus className="w-3 h-3 mr-1" />
                Add Rule
              </Button>
            </div>
          ) : (
            <Textarea
              value={String(items)}
              onChange={(e) => onChange({ ...content, [group]: e.target.value })}
              className="min-h-[60px] text-sm"
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Rules List Editor ({rules: string[]}) ────────────────────

function RulesListEditor({
  content,
  onChange,
}: {
  content: { rules: string[] };
  onChange: (c: any) => void;
}) {
  const rules = content.rules || [];

  return (
    <div className="space-y-2">
      {rules.map((rule, i) => (
        <div key={i} className="flex gap-2 items-start">
          <span className="text-xs text-muted-foreground font-mono mt-2 w-6 shrink-0">{i + 1}</span>
          <Textarea
            value={rule}
            onChange={(e) =>
              onChange({ ...content, rules: rules.map((r, j) => (j === i ? e.target.value : r)) })
            }
            className="min-h-[50px] text-sm"
          />
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 text-muted-foreground hover:text-destructive"
            onClick={() => onChange({ ...content, rules: rules.filter((_, j) => j !== i) })}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange({ ...content, rules: [...rules, "New rule..."] })}
      >
        <Plus className="w-3 h-3 mr-1" />
        Add Rule
      </Button>
    </div>
  );
}

// ─── Presence Template Editor ─────────────────────────────────

function PresenceTemplateEditor({
  content,
  onChange,
}: {
  content: Record<string, any>;
  onChange: (c: any) => void;
}) {
  return (
    <div className="space-y-6">
      {Object.entries(content).map(([level, templates]) => (
        <div key={level} className="border border-border/50 rounded-lg p-4">
          <h3 className="text-sm font-semibold capitalize mb-3">{level.replace(/_/g, " ")}</h3>
          <div className="space-y-3">
            {Object.entries(templates as Record<string, string>).map(([tplKey, tplValue]) => (
              <div key={tplKey}>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  {tplKey.replace(/_/g, " ")}
                </label>
                <Textarea
                  value={tplValue}
                  onChange={(e) =>
                    onChange({
                      ...content,
                      [level]: { ...(templates as any), [tplKey]: e.target.value },
                    })
                  }
                  className="min-h-[60px] text-sm"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Available placeholders: {"{household}"}, {"{signs}"}, {"{activity}"}, {"{interaction_clause}"}, {"{household_clause}"}
                </p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Fallback Raw JSON Editor ─────────────────────────────────

function RawJsonEditor({ content, onChange }: { content: any; onChange: (c: any) => void }) {
  const [raw, setRaw] = useState(JSON.stringify(content, null, 2));
  const [error, setError] = useState("");

  const handleChange = (value: string) => {
    setRaw(value);
    try {
      const parsed = JSON.parse(value);
      onChange(parsed);
      setError("");
    } catch (e) {
      setError(e instanceof SyntaxError ? e.message : "Invalid JSON");
    }
  };

  return (
    <div>
      <Textarea
        value={raw}
        onChange={(e) => handleChange(e.target.value)}
        className="min-h-[400px] font-mono text-xs"
      />
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
